import React, { useState } from 'react';
import { Input, Textarea } from '@/shared/components/ui';
import { Drawer } from '@/shared/components/ui/Drawer';
import { useCreateApiKey } from '../hooks';
import type { CreateApiKeyRequest } from '../types';
import { Icon } from '@/shared/icons'
/**
 * Modal de criação de API Key
 *
 * Features:
 * - Exibe chave completa apenas uma vez após criação
 * - Botão de copiar chave
 * - Always-light theme (independente do tema da aplicação)
 *
 * @example
 * <CreateApiKeyModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={(key) => handleSuccess(key)}
 * />
 */
export interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (key: any) => void;
}

export const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreateApiKeyRequest>({
    name: '',
    description: '',
    expiresAt: '',
  });
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createdMaskedKey, setCreatedMaskedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { mutate: createApiKey, isPending } = useCreateApiKey();

  const handleClose = () => {
    setFormData({ name: '', description: '', expiresAt: '' });
    setCreatedKey(null);
    setCreatedMaskedKey(null);
    setCopied(false);
    setFormErrors({});
    onClose();
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = 'Nome deve ter pelo menos 3 caracteres';
    }

    if (formData.expiresAt) {
      const expiryDate = new Date(formData.expiresAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (expiryDate < today) {
        errors.expiresAt = 'Data de expiração deve ser futura';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Transformar data para ISO 8601 se preenchida
    const payload: CreateApiKeyRequest = {
      ...formData,
      expiresAt: formData.expiresAt
        ? new Date(formData.expiresAt).toISOString()
        : undefined,
    };

    createApiKey(payload, {
      onSuccess: (response: any) => {
        setCreatedKey(response.key);  // Full key for copying
        setCreatedMaskedKey(response.maskedKey);  // Masked key for display
        if (onSuccess) {
          onSuccess(response);
        }
      },
      onError: (error: any) => {
        console.error('Erro ao criar API Key:', error);
        setFormErrors({ submit: error.message || 'Erro ao criar API Key' });
      },
    });
  };

  const handleCopyKey = async () => {
    if (!createdKey) return;

    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar chave:', error);
    }
  };

  const formFooter = (
    <>
      <button
        type="button"
        onClick={handleClose}
        className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors"
        style={{
          color: '#1e293b',
          backgroundColor: '#ffffff',
          borderColor: '#e5e7eb',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f1f5f9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#ffffff';
        }}
      >
        Cancelar
      </button>

      <button
        type="submit"
        form="create-api-key-form"
        disabled={isPending}
        className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
        style={{
          backgroundColor: '#8b5cf6',
        }}
        onMouseEnter={(e) => {
          if (!isPending) {
            e.currentTarget.style.backgroundColor = '#7c3aed';
          }
        }}
        onMouseLeave={(e) => {
          if (!isPending) {
            e.currentTarget.style.backgroundColor = '#8b5cf6';
          }
        }}
      >
        {isPending ? 'Criando...' : 'Criar API Key'}
      </button>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      icon={<Icon name="key" className="w-5 h-5" />}
      title={createdKey ? 'API Key Criada' : 'Gerar chave de API'}
      disabled={isPending}
      footer={createdKey ? null : formFooter}
    >
        {/* Content */}
        {createdKey ? (
          // Success: Show created key
          <div className="p-6">
            <div
              className="p-4 rounded-lg mb-4"
              style={{
                backgroundColor: 'rgba(52, 211, 153, 0.1)',
                border: '1px solid rgba(52, 211, 153, 0.3)',
              }}
            >
              <div className="flex items-start gap-3 mb-3">
                <Icon name="alert-circle" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                <div>
                  <h4 className="text-sm font-semibold mb-1" style={{ color: '#14532d' }}>
                    Atenção: Salve a chave agora!
                  </h4>
                  <p className="text-sm" style={{ color: '#15803d' }}>
                    Esta é a única vez que a chave completa será exibida. Copie e salve em local seguro.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                Seu Token
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={createdMaskedKey || createdKey}
                  readOnly
                  className="flex-1 font-mono"
                />
                <button
                  onClick={handleCopyKey}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: copied ? '#10b981' : '#8b5cf6',
                    color: '#ffffff',
                  }}
                  onMouseEnter={(e) => {
                    if (!copied) {
                      e.currentTarget.style.backgroundColor = '#7c3aed';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!copied) {
                      e.currentTarget.style.backgroundColor = '#8b5cf6';
                    }
                  }}
                >
                  {copied ? (
                    <>
                      <Icon name="check" className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <Icon name="copy" className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>

            <div
              className="p-3 rounded-lg mb-4"
              style={{ backgroundColor: '#f1f5f9', borderColor: '#e5e7eb' }}
            >
              <p className="text-xs" style={{ color: '#64748b' }}>
                <span className="font-semibold">Nome:</span> {formData.name}
              </p>
              {formData.description && (
                <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                  <span className="font-semibold">Descrição:</span> {formData.description}
                </p>
              )}
              {formData.expiresAt && (
                <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                  <span className="font-semibold">Expira em:</span>{' '}
                  {new Date(formData.expiresAt).toLocaleDateString('pt-BR')}
                </p>
              )}
            </div>

            <button
              onClick={handleClose}
              className="w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors"
              style={{
                backgroundColor: '#8b5cf6',
                color: '#ffffff',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#7c3aed';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#8b5cf6';
              }}
            >
              Concluir
            </button>
          </div>
        ) : (
          // Form: Create new key
          <form id="create-api-key-form" onSubmit={handleSubmit} className="p-6">
            {/* Error Alert */}
            {formErrors.submit && (
              <div
                className="p-3 rounded-lg mb-4"
                style={{
                  backgroundColor: 'rgba(248, 113, 113, 0.1)',
                  border: '1px solid rgba(248, 113, 113, 0.3)',
                }}
              >
                <p className="text-sm" style={{ color: '#b91c1c' }}>{formErrors.submit}</p>
              </div>
            )}

            {/* Name Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                Nome <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) {
                    setFormErrors({ ...formErrors, name: '' });
                  }
                }}
                placeholder="Ex: API Produção"
                error={formErrors.name}
              />
            </div>

            {/* Description Field */}
            <div className="mb-4">
              <Textarea
                label="Descrição (opcional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição da API Key..."
                rows={3}
                resize="none"
              />
            </div>

            {/* Expiration Date Field */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                Data de Expiração (opcional)
              </label>
              <Input
                type="date"
                value={formData.expiresAt}
                onChange={(e) => {
                  setFormData({ ...formData, expiresAt: e.target.value });
                  if (formErrors.expiresAt) {
                    setFormErrors({ ...formErrors, expiresAt: '' });
                  }
                }}
                error={formErrors.expiresAt}
              />
              <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                Deixe em branco para chave sem expiração
              </p>
            </div>

            {/* Aviso: chave exibida uma única vez (âmbar, igual ao mockup) */}
            <div
              className="p-3 rounded-lg"
              style={{ backgroundColor: '#fef3c7', color: '#92400e' }}
            >
              <p className="text-xs">
                A chave completa será exibida <strong>uma única vez</strong> após a criação. Guarde-a com segurança.
              </p>
            </div>

          </form>
        )}
    </Drawer>
  );
};
